import React, { useCallback, useEffect, useState } from 'react';
import Icon from '../../components/Icon/Icon';
import storeService from '../../services/store.service';
import { PublishedTemplatePreview } from '../../types/store.types';

type DetailTab =
  | 'summary'
  | 'roles'
  | 'statuses'
  | 'fields'
  | 'integrations'
  | 'searches'
  | 'imports'
  | 'workflows'
  | 'commissions';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'summary', label: 'Résumé' },
  { id: 'roles', label: 'Rôles' },
  { id: 'statuses', label: 'Statuts' },
  { id: 'fields', label: 'Champs' },
  { id: 'integrations', label: 'Intégrations' },
  { id: 'searches', label: 'Recherches' },
  { id: 'imports', label: 'Imports' },
  { id: 'workflows', label: 'Automatisations' },
  { id: 'commissions', label: 'Commissions' },
];

interface TemplateDetailModalProps {
  open: boolean;
  templateId: number | null;
  templateName: string;
  onClose: () => void;
}

const TemplateDetailModal: React.FC<TemplateDetailModalProps> = ({
  open,
  templateId,
  templateName,
  onClose,
}) => {
  const [tab, setTab] = useState<DetailTab>('summary');
  const [preview, setPreview] = useState<PublishedTemplatePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (templateId == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await storeService.getPublishedTemplatePreview(templateId);
      const p = res.data?.preview;
      if (!p) {
        setError('Aperçu indisponible.');
        setPreview(null);
        return;
      }
      setPreview(p);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message || 'Impossible de charger le détail du modèle.';
      setError(msg);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (!open || templateId == null) {
      return;
    }
    setTab('summary');
    load();
  }, [open, templateId, load]);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setError(null);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className="cst-template-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="cst-template-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cst-template-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cst-template-modal__head">
          <div>
            <h2 id="cst-template-modal-title" className="cst-template-modal__title">
              Détail du modèle
            </h2>
            <p className="cst-template-modal__subtitle">{templateName}</p>
          </div>
          <button
            type="button"
            className="cst-template-modal__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        <nav className="cst-template-modal__tabs" aria-label="Sections du paramétrage">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`cst-template-modal__tab ${tab === t.id ? 'cst-template-modal__tab--active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="cst-template-modal__body">
          {loading && (
            <div className="cst-template-modal__loading">
              <Icon name="loader" size={22} />
              <span>Chargement…</span>
            </div>
          )}
          {!loading && error && (
            <div className="cst-template-modal__error">
              <Icon name="alert-circle" size={18} />
              <span>{error}</span>
            </div>
          )}
          {!loading && !error && preview && (
            <div className="cst-template-modal__panel">
              {tab === 'summary' && <SummaryPanel preview={preview} />}
              {tab === 'roles' && <RolesPanel preview={preview} />}
              {tab === 'statuses' && <StatusesPanel preview={preview} />}
              {tab === 'fields' && <FieldsPanel preview={preview} />}
              {tab === 'integrations' && <IntegrationsPanel preview={preview} />}
              {tab === 'searches' && <SearchesPanel preview={preview} />}
              {tab === 'imports' && <ImportsPanel preview={preview} />}
              {tab === 'workflows' && <WorkflowsPanel preview={preview} />}
              {tab === 'commissions' && <CommissionsPanel preview={preview} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function SummaryPanel({ preview }: { preview: PublishedTemplatePreview }) {
  return (
    <div className="cst-template-modal__summary">
      <ul className="cst-template-modal__counts">
        <li>
          <span>Rôles</span> <strong>{preview.roles.length}</strong>
        </li>
        <li>
          <span>Statuts</span> <strong>{preview.orderStatuses.length}</strong>
        </li>
        <li>
          <span>Champs</span> <strong>{preview.customFields.length}</strong>
        </li>
        <li>
          <span>Intégrations</span> <strong>{preview.integrations.length}</strong>
        </li>
        <li>
          <span>Groupes de recherches</span> <strong>{preview.searchGroups.length}</strong>
        </li>
        <li>
          <span>Recherches enregistrées</span> <strong>{preview.savedSearches.length}</strong>
        </li>
        <li>
          <span>Modèles d&apos;import</span> <strong>{preview.importTemplates.length}</strong>
        </li>
        <li>
          <span>Automatisations</span> <strong>{preview.workflows.length}</strong>
        </li>
        <li>
          <span>Règles commission</span> <strong>{preview.commissionRules.length}</strong>
        </li>
        <li>
          <span>Règles bénéficiaires</span>{' '}
          <strong>{preview.commissionBeneficiaryRules.length}</strong>
        </li>
      </ul>
    </div>
  );
}

function RolesPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.roles.length === 0) {
    return <p className="cst-template-modal__empty">Aucun rôle dans ce modèle.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Nom</th>
          <th>Système</th>
          <th>Permissions</th>
        </tr>
      </thead>
      <tbody>
        {preview.roles.map((r) => (
          <tr key={r.name}>
            <td>
              <div className="cst-template-table__main">{r.name}</div>
              {r.description && (
                <div className="cst-template-table__sub">{r.description}</div>
              )}
            </td>
            <td>{r.is_system ? 'Oui' : 'Non'}</td>
            <td>{r.permission_count}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusesPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.orderStatuses.length === 0) {
    return <p className="cst-template-modal__empty">Aucun statut de commande.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Libellé</th>
          <th>Clé</th>
          <th>Actif</th>
        </tr>
      </thead>
      <tbody>
        {preview.orderStatuses.map((s) => (
          <tr key={`${s.key}-${s.position}`}>
            <td>{s.label || s.name}</td>
            <td>
              <code className="cst-template-table__code">{s.key}</code>
            </td>
            <td>{s.is_active ? 'Oui' : 'Non'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FieldsPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.customFields.length === 0) {
    return <p className="cst-template-modal__empty">Aucun champ personnalisé.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Nom</th>
          <th>Clé</th>
          <th>Type</th>
          <th>Catégorie</th>
        </tr>
      </thead>
      <tbody>
        {preview.customFields.map((f) => (
          <tr key={f.key}>
            <td>{f.name}</td>
            <td>
              <code className="cst-template-table__code">{f.key}</code>
            </td>
            <td>{f.type}</td>
            <td>{f.category}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function IntegrationsPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.integrations.length === 0) {
    return <p className="cst-template-modal__empty">Aucune intégration configurée dans le modèle.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Intégration</th>
          <th>Clé</th>
          <th>État (modèle)</th>
        </tr>
      </thead>
      <tbody>
        {preview.integrations.map((i, idx) => (
          <tr key={`${i.integration_key}-${idx}`}>
            <td>{i.integration_name}</td>
            <td>
              <code className="cst-template-table__code">{i.integration_key}</code>
            </td>
            <td>{i.status || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SearchesPanel({ preview }: { preview: PublishedTemplatePreview }) {
  return (
    <>
      <h3 className="cst-template-modal__section-title">Groupes</h3>
      {preview.searchGroups.length === 0 ? (
        <p className="cst-template-modal__empty">Aucun groupe.</p>
      ) : (
        <ul className="cst-template-modal__list">
          {preview.searchGroups.map((g) => (
            <li key={`${g.name}-${g.position}`}>
              {g.name} <span className="cst-template-modal__muted">(position {g.position})</span>
            </li>
          ))}
        </ul>
      )}
      <h3 className="cst-template-modal__section-title">Recherches enregistrées</h3>
      {preview.savedSearches.length === 0 ? (
        <p className="cst-template-modal__empty">Aucune recherche enregistrée.</p>
      ) : (
        <table className="cst-template-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Groupe</th>
              <th>Tableau de bord</th>
            </tr>
          </thead>
          <tbody>
            {preview.savedSearches.map((s) => (
              <tr key={`${s.name}-${s.position}`}>
                <td>{s.name}</td>
                <td>{s.group_name || '—'}</td>
                <td>{s.is_dashboard_visible ? 'Oui' : 'Non'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function ImportsPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.importTemplates.length === 0) {
    return <p className="cst-template-modal__empty">Aucun modèle d&apos;import.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Nom</th>
          <th>Entité</th>
          <th>Fichier</th>
          <th>Actif</th>
        </tr>
      </thead>
      <tbody>
        {preview.importTemplates.map((t, idx) => (
          <tr key={`${t.name}-${idx}`}>
            <td>{t.name}</td>
            <td>{t.entity}</td>
            <td>{t.file_type}</td>
            <td>{t.is_active ? 'Oui' : 'Non'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function WorkflowsPanel({ preview }: { preview: PublishedTemplatePreview }) {
  if (preview.workflows.length === 0) {
    return <p className="cst-template-modal__empty">Aucune automatisation.</p>;
  }
  return (
    <table className="cst-template-table">
      <thead>
        <tr>
          <th>Nom</th>
          <th>Actif</th>
        </tr>
      </thead>
      <tbody>
        {preview.workflows.map((w, idx) => (
          <tr key={`${w.name}-${idx}`}>
            <td>
              <div className="cst-template-table__main">{w.name}</div>
              {w.description && (
                <div className="cst-template-table__sub">{w.description}</div>
              )}
            </td>
            <td>{w.is_active ? 'Oui' : 'Non'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CommissionsPanel({ preview }: { preview: PublishedTemplatePreview }) {
  return (
    <>
      <h3 className="cst-template-modal__section-title">Règles de commission</h3>
      {preview.commissionRules.length === 0 ? (
        <p className="cst-template-modal__empty">Aucune règle.</p>
      ) : (
        <table className="cst-template-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Type</th>
              <th>Actif</th>
            </tr>
          </thead>
          <tbody>
            {preview.commissionRules.map((r, idx) => (
              <tr key={`${r.name}-${r.priority}-${idx}`}>
                <td>{r.name}</td>
                <td>
                  {r.rule_type}
                  {r.rule_type === 'product' ? (
                    <span className="cst-template-modal__muted"> (non copié)</span>
                  ) : null}
                </td>
                <td>{r.is_active ? 'Oui' : 'Non'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="cst-template-modal__section-title">Règles bénéficiaires</h3>
      {preview.commissionBeneficiaryRules.length === 0 ? (
        <p className="cst-template-modal__empty">Aucune règle bénéficiaire.</p>
      ) : (
        <table className="cst-template-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Statut déclencheur</th>
              <th>Type bénéficiaire</th>
            </tr>
          </thead>
          <tbody>
            {preview.commissionBeneficiaryRules.map((r, idx) => (
              <tr key={`${r.name}-${r.priority}-${idx}`}>
                <td>{r.name}</td>
                <td>{r.trigger_source === 'delivery_status' ? 'Livraison' : 'Commande'}: {r.trigger_status}</td>
                <td>{r.beneficiary_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default TemplateDetailModal;
